import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ChooseTemplateTab from "../../components/event/ChooseTemplateTab";
import AddDetailsTab from "../../components/event/AddDetailsTab";
import FinalTouchUpTab from "../../components/event/FinalTouchUpTab";
import PreviewTab from "../../components/event/PreviewTab";
import EventSocialPost from "../../components/event/EventSocialPost";
import EventService from "../../services/EventService";
import { useEventStore } from "../../store/eventStore";

const CREATE_FLOW_KEY = "event-flow:create";
const CREATE_TAB_KEY = "event-tab:create";

const getEditFlowKey = (eventId) => `event-flow:edit:${eventId}`;
const getEditTabKey = (eventId) => `event-tab:edit:${eventId}`;

const EventCreation = () => {
  const { eventId } = useParams();
  const isEditMode = Boolean(eventId);

  const [activeTab, setActiveTab] = useState("ChooseTemplate");
  const [isInitialized, setIsInitialized] = useState(false);
  const addDetailsValidatorRef = useRef(null);
  const finalTouchUpValidatorRef = useRef(null);

  const selectedTemplate = useEventStore((state) => state.selectedTemplate);
  const eventDetails = useEventStore((state) => state.eventDetails);
  const canvasData = useEventStore((state) => state.canvasData);
  const isDirty = useEventStore((state) => state.isDirty);
  const lastSavedAt = useEventStore((state) => state.lastSavedAt);
  const setMode = useEventStore((state) => state.setMode);
  const setEventId = useEventStore((state) => state.setEventId);
  const hydrateFromSnapshot = useEventStore((state) => state.hydrateFromSnapshot);
  const loadEventData = useEventStore((state) => state.loadEventData);
  const resetEvent = useEventStore((state) => state.resetEvent);

  const flowStorageKey = useMemo(
    () => (isEditMode ? getEditFlowKey(eventId) : CREATE_FLOW_KEY),
    [eventId, isEditMode],
  );

  const tabStorageKey = useMemo(
    () => (isEditMode ? getEditTabKey(eventId) : CREATE_TAB_KEY),
    [eventId, isEditMode],
  );

  const trySwitchTab = (nextTab) => {
    if (activeTab === "AddDetails" && addDetailsValidatorRef.current) {
      const isValid = addDetailsValidatorRef.current();
      if (!isValid) return;
    }

    if (activeTab === "FinalTouchUp" && finalTouchUpValidatorRef.current) {
      const isValid = finalTouchUpValidatorRef.current();
      if (!isValid) return;
    }

    setActiveTab(nextTab);
  };

  useEffect(() => {
    if (window.location.hash === "#_=_") {
      window.history.replaceState(null, null, window.location.href.split("#")[0]);
    }
  }, []);

  useEffect(() => {
    let disposed = false;
    setIsInitialized(false);

    const initializeFlow = async () => {
      const defaultTab = isEditMode ? "AddDetails" : "ChooseTemplate";
      const savedTab = sessionStorage.getItem(tabStorageKey);
      setActiveTab(savedTab || defaultTab);

      if (isEditMode) {
        setMode("edit");
        setEventId(eventId);

        const savedFlow = sessionStorage.getItem(flowStorageKey);
        if (savedFlow) {
          try {
            const parsed = JSON.parse(savedFlow);
            hydrateFromSnapshot(parsed, "edit", eventId);
            if (!disposed) setIsInitialized(true);
            return;
          } catch (error) {
            console.error("Failed to restore event flow from session storage:", error);
            sessionStorage.removeItem(flowStorageKey);
          }
        }

        try {
          const response = await EventService.fetchDraftEventById(eventId);
          if (!disposed && response.data?.success) {
            loadEventData(response.data.data);
          }
        } catch (error) {
          console.error("Failed to load event data:", error);
        }

        if (!disposed) setIsInitialized(true);
        return;
      }

      setMode("create");
      setEventId(null);

      const savedFlow = sessionStorage.getItem(CREATE_FLOW_KEY);
      if (savedFlow) {
        try {
          const parsed = JSON.parse(savedFlow);
          hydrateFromSnapshot(parsed, "create", null);
          if (!disposed) setIsInitialized(true);
          return;
        } catch (error) {
          console.error("Failed to restore create flow from session storage:", error);
          sessionStorage.removeItem(CREATE_FLOW_KEY);
        }
      }

      resetEvent();
      if (!disposed) setIsInitialized(true);
    };

    initializeFlow();

    return () => {
      disposed = true;
    };
  }, [
    eventId,
    flowStorageKey,
    hydrateFromSnapshot,
    isEditMode,
    loadEventData,
    resetEvent,
    setEventId,
    setMode,
    tabStorageKey,
  ]);

  useEffect(() => {
    if (!isInitialized) return;
    sessionStorage.setItem(tabStorageKey, activeTab);
  }, [activeTab, isInitialized, tabStorageKey]);

  useEffect(() => {
    if (!isInitialized) return;
    const snapshot = {
      selectedTemplate,
      eventDetails,
      canvasData,
      isDirty,
      lastSavedAt,
    };
    sessionStorage.setItem(flowStorageKey, JSON.stringify(snapshot));
  }, [
    canvasData,
    eventDetails,
    flowStorageKey,
    isDirty,
    isInitialized,
    lastSavedAt,
    selectedTemplate,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const handleTemplateSelect = () => {
    setActiveTab("AddDetails");
  };

  const handleViewEvent = () => {
    setActiveTab("Preview");
  };

  const renderTabComponent = () => {
    switch (activeTab) {
      case "ChooseTemplate":
        return (
          <ChooseTemplateTab
            onTemplateSelect={handleTemplateSelect}
            onViewEvent={handleViewEvent}
          />
        );
      case "AddDetails":
        return (
          <AddDetailsTab
            onBack={() => setActiveTab("ChooseTemplate")}
            onNext={() => setActiveTab("FinalTouchUp")}
            registerValidator={(fn) => {
              addDetailsValidatorRef.current = fn();
            }}
          />
        );
      case "FinalTouchUp":
        return (
          <FinalTouchUpTab
            onBack={() => setActiveTab("AddDetails")}
            onNext={() => setActiveTab("Preview")}
            registerValidator={(fn) => {
              finalTouchUpValidatorRef.current = fn;
            }}
          />
        );
      case "Preview":
        return (
          <PreviewTab
            onBack={() => setActiveTab("FinalTouchUp")}
            onNext={() => setActiveTab("CreateAds")}
          />
        );
      case "CreateAds":
        return <EventSocialPost />;
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="tab-header">
        <TabButton
          label="Choose Templates"
          tabKey="ChooseTemplate"
          activeTab={activeTab}
          onClick={trySwitchTab}
        />
        <TabButton
          label="Add Details"
          tabKey="AddDetails"
          activeTab={activeTab}
          onClick={trySwitchTab}
        />
        <TabButton
          label="Final Touch-up"
          tabKey="FinalTouchUp"
          activeTab={activeTab}
          onClick={trySwitchTab}
        />
        <TabButton
          label="Preview"
          tabKey="Preview"
          activeTab={activeTab}
          onClick={trySwitchTab}
        />
        <TabButton
          label="Post to Social"
          tabKey="CreateAds"
          activeTab={activeTab}
          onClick={trySwitchTab}
        />
      </div>

      <div className="tab-content mt-3">{renderTabComponent()}</div>
    </div>
  );
};

const TabButton = ({ label, tabKey, activeTab, onClick }) => (
  <button
    className={`tab-btn ${activeTab === tabKey ? "active" : ""}`}
    onClick={() => onClick(tabKey)}
  >
    {label}
  </button>
);

export default EventCreation;
